import { Button, Fade, Stack, Typography, useTheme } from "@mui/material";
import { ControlledBigTooltip } from "components/ControlledBigTooltip";
import pluralize from "pluralize";
import { useState } from "react";

import { RecordDivergence } from "./formData";

/**
 * The list of everything this review disagrees with the record about, beside the
 * validation issue list.
 *
 * A long form hides its own indicators: the per-field icons say which values are
 * at stake only once you scroll past them, and the banner says how many without
 * saying which. This is the same "here they all are" affordance `IssueListButton`
 * gives validation, in warning rather than error colours — a disagreement is a
 * question for the reviewer, not a rule they have broken, and rendering it
 * through the issue list itself would mark every conflicted field as a
 * validation failure (`useValidatingForm` turns an issue into a field error).
 */
export function RecordDivergenceListButton({ divergence }: { divergence: RecordDivergence }) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);

  const lines = [
    ...divergence.conflicts.map(
      (conflict) =>
        `${conflict.label} — on the record: ${conflict.recordDisplay}; this review: ${conflict.reviewDisplay}`
    ),
    ...divergence.recordRows.map(
      (row) => `${row.label} is on the record, but this document does not list it`
    ),
  ];
  if (lines.length === 0) return null;

  const header = `${lines.length} Record ${pluralize("Difference", lines.length)}`;

  return (
    <ControlledBigTooltip
      open={open}
      onClose={() => setOpen(false)}
      placement="top-end"
      slots={{ transition: Fade }}
      slotProps={{ tooltip: { sx: { maxWidth: theme.spacing(49) } } }}
      title={
        <Stack gap={1} sx={{ maxHeight: theme.spacing(75), overflowY: "auto" }}>
          <Typography variant="subtitle1" fontWeight="bold" fontSize="12px">
            {header}
          </Typography>
          <Typography component="ul" sx={{ pl: 2, fontSize: "12px", "& li": { mb: 0.5 } }}>
            {lines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </Typography>
        </Stack>
      }
    >
      <Button
        type="button"
        color="warning"
        variant="text"
        onClick={() => setOpen((wasOpen) => !wasOpen)}
      >
        {header}
      </Button>
    </ControlledBigTooltip>
  );
}
